import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, Chip, LinearProgress, MenuItem, Select } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import { ui, tone } from '../../../utils/uiTokens'
import { money, SALARY_DEFAULTS } from '../../../utils/salaryFormulas'
import { salaryApiSlice } from '../../../features/salary/salarySlice'
import SalaryDetailView from '../../admin/mentorSalaryPage/components/SalaryDetailView'
import SalaryFactorRules from '../../admin/mentorSalaryPage/components/SalaryFactorRules'
import { Content, Wrapper } from '../../admin/mentorSalaryPage/styles'
import ApprovalBadge from '../../admin/mentorSalaryPage/components/ApprovalBadge'
import { dateRange, ddmm } from '../../../utils/periodDates'

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9]
const WEEKS = [1, 2, 3, 4]

const MySalaryPage: React.FC = () => {
    const { formatMessage } = useIntl()
    const [cycleNumber, setCycleNumber] = useState<number>(1)
    const [weekNumber, setWeekNumber] = useState<number>(0)
    const [subjectId, setSubjectId] = useState<number>(0)
    const [groupId, setGroupId] = useState<number>(0)

    const { data, isFetching } = salaryApiSlice.useGetMySalaryQuery({
        cycleNumber,
        courseSubjectId: subjectId,
        groupId,
        weekNumber,
    })
    const isClosed = data?.cycle?.status === 'CLOSED'
    const isWeek = weekNumber !== 0

    // при первой загрузке бэкенд сам выбирает поток — синхронизируем селект
    useEffect(() => {
        if (subjectId === 0 && data?.courseSubjectId) setSubjectId(data.courseSubjectId)
    }, [data, subjectId])

    const maxTrend = useMemo(() => Math.max(1, ...(data?.trend ?? []).map((t) => t.total)), [data])

    // Одно поле "курс · предмет · группа": в одном курсе у ментора бывает ДЖТ и английский,
    // по одному курсу строки выглядели одинаково. Значение = "courseSubjectId:groupId", 0 = все группы
    const options = useMemo(
        () =>
            (data?.potoks ?? []).flatMap((p) => {
                const base = [p.titleKz || p.titleRu, p.subjectTitleKz || p.subjectTitleRu].filter(Boolean).join(' · ')
                const groups = p.groups ?? []
                const all = groups.length === 1 ? [] : [{ value: `${p.courseSubjectId}:0`, label: `${base} · ${formatMessage({ id: 'allGroups' })}` }]
                return [...all, ...groups.map((g) => ({ value: `${p.courseSubjectId}:${g.groupId}`, label: `${base} · ${g.title}` }))]
            }),
        [data, formatMessage]
    )
    const selected = options.some((o) => o.value === `${subjectId}:${groupId}`)
        ? `${subjectId}:${groupId}`
        : options.find((o) => o.value.startsWith(`${subjectId}:`))?.value ?? ''

    const onPick = (value: string) => {
        const [cs, g] = value.split(':').map(Number)
        setSubjectId(cs)
        setGroupId(g)
    }

    return (
        <Wrapper>
            <Content>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, marginBottom: '16px' }}>
                    <Box>
                        <H3>{formatMessage({ id: 'salaryMySalary' })}</H3>
                        <Box sx={{ color: ui.sub, fontSize: '13.5px', marginTop: '6px', maxWidth: 560 }}>
                            {formatMessage({ id: 'salaryMySalarySub' })}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        {data && !isWeek && <ApprovalBadge status={data.approvalStatus} />}
                        {data?.paid && !isWeek && (
                            <Chip
                                label={`${formatMessage({ id: 'dirPaidCol' })}${data.paidAt ? ` · ${ddmm(data.paidAt)}` : ''}`}
                                sx={{ fontWeight: 700, backgroundColor: tone.ok.bg, color: tone.ok.text }}
                            />
                        )}
                        <Chip
                            label={formatMessage({ id: isClosed ? 'salaryStatusClosed' : 'salaryStatusOpen' })}
                            sx={{ fontWeight: 700, backgroundColor: isClosed ? ui.track : tone.ok.bg, color: isClosed ? ui.sub : tone.ok.text }}
                        />
                    </Box>
                </Box>

                {/* пән басшы вернул жалақы: ментор видит, что исправить */}
                {data?.approvalStatus === 'RETURNED' && !isWeek && (
                    <Box sx={returnedBanner}>
                        <Box sx={{ fontWeight: 700 }}>{formatMessage({ id: 'approvalReturnedBanner' })}</Box>
                        <Box sx={{ marginTop: '4px' }}>{data.approvalComment}</Box>
                    </Box>
                )}

                <Box sx={filterBar}>
                    <Field label={formatMessage({ id: 'salaryPotok' })}>
                        <Select size="small" value={selected} onChange={(e) => onPick(String(e.target.value))} sx={{ ...selectSx, minWidth: { xs: 0, sm: 360 }, maxWidth: '100%' }} displayEmpty>
                            {selected === '' && <MenuItem value="">—</MenuItem>}
                            {options.map((o) => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </Select>
                    </Field>

                    <Field label={formatMessage({ id: 'month' })}>
                        <Select size="small" value={cycleNumber} onChange={(e) => setCycleNumber(Number(e.target.value))} sx={selectSx}>
                            {MONTHS.map((m) => (
                                <MenuItem key={m} value={m}>{m} {formatMessage({ id: 'month' })}</MenuItem>
                            ))}
                        </Select>
                    </Field>

                    <Field label={formatMessage({ id: 'week' })}>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                            <Box sx={weekTab(weekNumber === 0)} onClick={() => setWeekNumber(0)}>
                                {formatMessage({ id: 'allWeeks' })}
                            </Box>
                            {WEEKS.map((w) => (
                                <Box key={w} sx={weekTab(weekNumber === w)} onClick={() => setWeekNumber(w)}>
                                    {w} {formatMessage({ id: 'week' })}
                                </Box>
                            ))}
                        </Box>
                    </Field>
                </Box>

                {/* даты из календаря сервис басшысы: «5 ай · 21.09–18.10 · жалақы беру 19.10–23.10» */}
                {data?.periodStart && (
                    <Box sx={{ color: ui.sub, fontSize: '13.5px', margin: '-6px 0 14px' }}>
                        <b style={{ color: ui.ink }}>{cycleNumber} {formatMessage({ id: 'month' }).toLowerCase()}</b> · {dateRange(data.periodStart, data.periodEnd)}
                        {data.payFrom && ` · ${formatMessage({ id: 'dirPayWindow' })} ${dateRange(data.payFrom, data.payTo)}`}
                    </Box>
                )}

                {isFetching || !data ? (
                    <LinearProgress />
                ) : (
                    <>
                        <Box sx={surveyLine}>
                            <Box>
                                <Box sx={{ fontWeight: 600, color: ui.ink }}>{formatMessage({ id: 'salarySurveyLabel' })}</Box>
                                {isWeek && (
                                    <Box sx={{ fontSize: '12px', color: ui.muted, marginTop: '2px' }}>
                                        {formatMessage({ id: 'salaryWeekNote' })}
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ fontWeight: 800, fontSize: '18px', color: ui.pink }}>{data.surveyPercent}%</Box>
                        </Box>

                        <SalaryDetailView detail={data} editable={false} weekMode={isWeek} weekNumber={weekNumber} />

                        {!isWeek && (data.trend?.length ?? 0) > 1 && (
                            <Box sx={{ marginTop: '26px' }}>
                                <H3>{formatMessage({ id: 'salaryTrend' })}</H3>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', marginTop: '14px', height: 140 }}>
                                    {data.trend.map((t) => (
                                        <Box key={t.cycleNumber} sx={{ flex: 1, textAlign: 'center' }}>
                                            <Box sx={{ fontSize: '12px', fontWeight: 700, color: ui.ink, marginBottom: '4px' }}>{money(t.total)}</Box>
                                            <Box sx={{
                                                height: `${Math.round((t.total / maxTrend) * 100)}px`,
                                                minHeight: 4,
                                                backgroundColor: t.cycleNumber === cycleNumber ? ui.pink : ui.pinkSoft,
                                                borderRadius: '8px 8px 0 0',
                                            }} />
                                            <Box sx={{ fontSize: '12px', color: ui.sub, marginTop: '6px' }}>{t.cycleNumber} {formatMessage({ id: 'month' })}</Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Box sx={{ marginTop: '28px' }}>
                            <H3>{formatMessage({ id: 'salaryRules' })}</H3>
                            <Box sx={{ marginTop: '14px' }}>
                                <SalaryFactorRules config={SALARY_DEFAULTS} />
                            </Box>
                        </Box>

                        <Box sx={{ color: ui.muted, fontSize: '12px', marginTop: '16px' }}>
                            {formatMessage({ id: 'salaryMentorFooter' })}
                        </Box>
                    </>
                )}
            </Content>
        </Wrapper>
    )
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', color: ui.muted, textTransform: 'uppercase' }}>{label}</Box>
        {children}
    </Box>
)

const filterBar = {
    display: 'flex',
    gap: 2,
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    padding: '14px 16px',
    border: `1px solid ${ui.border}`,
    borderRadius: ui.radius,
    marginBottom: '18px',
}

const selectSx = {
    minWidth: 190,
    borderRadius: ui.radius,
    fontSize: '13.5px',
    fontWeight: 600,
    backgroundColor: ui.card,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: ui.border },
}

const weekTab = (active: boolean) => ({
    padding: '8px 14px',
    borderRadius: ui.radius,
    cursor: 'pointer',
    userSelect: 'none',
    fontSize: '13px',
    lineHeight: 1,
    fontWeight: active ? 700 : 500,
    color: active ? ui.pink : ui.sub,
    backgroundColor: active ? ui.pinkSoft : 'transparent',
    border: `1px solid ${active ? 'transparent' : ui.border}`,
    '&:hover': { borderColor: ui.pink, color: ui.pink },
})

const returnedBanner = {
    border: `1px solid ${tone.bad.border}`,
    backgroundColor: tone.bad.bg,
    color: tone.bad.text,
    borderRadius: ui.radius,
    padding: '12px 16px',
    marginBottom: '16px',
    fontSize: '13.5px',
}

const surveyLine = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
    border: `1px solid ${ui.border}`,
    borderRadius: ui.radius,
    padding: '14px 18px',
    backgroundColor: ui.surface,
    marginBottom: '18px',
}

export default MySalaryPage
